import { Box, boxClasses, Button, ButtonGroup, CircularProgress, Dialog, Drawer, IconButton, MenuItem, Popover, Radio, Select, SwipeableDrawer, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { observer } from 'mobx-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Icons, convertToCDNLink, formatDate, getGroupChipLabel, getTransformedGroupDetails, isIOS } from '../../utils/utilities'
import noSessionsImg from '../../assets/noclasses.svg'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useLiveSessionStore } from '../../store/livesessions.store'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import { useUserStore } from '../../store/user.store'
import CreateClassSessionDialog from '../../components/presenter/live-session/CreateClassSessionDialog'
import api from '../../service/api'
import { toast } from 'react-toastify'
import VideoPlayer from '../../components/common/video-player/VideoPlayer'
import DocumentViewer from '../../components/common/DocumentViewer'
import AddResourcesDialog from '../../components/presenter/live-session/AddResourcesDialog'
import noResourcesImg from '../../assets/upload-assignment.svg'
import CustomSwitch from '../../components/common/CustomSwitch'
import TryAgainMessageBlock from '../../components/common/TryAgainMessageBlock'
import CustomChip from '../../components/common/CustomChip'



const getAccessGroups = groups => {
    let groupsNames = []
    for ( let i = 0; i < groups.length; i++ ) {
        const group = groups[i]
        let title = ''
        if ( group.section_assignment_id ) {
            title = `${group.course_branch_short_name ? group.course_branch_short_name + "," : ""} ${group?.current_year ? `Y${group?.current_year},` : ""} ${group.current_sem ? `S${group.current_sem},` : ""} Sec: ${group.section_short_name}`
        } else
            title = `${group.course_name_short ? group.course_name_short + "," : ""} ${group?.current_year ? `Y${group?.current_year},` : ""} ${group.current_sem ? `S${group.current_sem},` : ""} Batch: ${group.batch_short_name}`
        groupsNames.push( title )
    }
    return groupsNames
}

const ClassSessionDetails = observer( ( { deleteSession, classSession, copyLink, handleClickOnVideoResource, viewResource } ) => {

    const [loading, setLoading] = useState( true )
    const [downloading, setDownloading] = useState( false )
    const [enabling, setEnabling] = useState( false )
    const [deleting, setDeleting] = useState( false )
    const [addOptionsOpen, setAddOptionsOpen] = useState( false )
    const [selectedAddOption, setSelectedAddOption] = useState( 0 )
    const [editNewDialogState, setEditNewDialogState] = useState( false )
    const [deleteConfirmationDialogState, setDeleteConfirmationDialogState] = useState( false )
    const [uploadResourcesDialogState, setUploadResourcesDialogState] = useState( false )

    const { layout, border, palette } = useTheme()

    const LiveSessionsStore = useLiveSessionStore()
    const UserStore = useUserStore()

    const navigate = useNavigate()

    const addOptionsRef = useRef()

    const handleMenuItemClick = ( event, index, action ) => {
        setSelectedAddOption( index )
        setAddOptionsOpen( false )
        if ( action ) action()
    }

    const addOptions = [
        {
            icon: Icons.default.AddCircleIcon,
            label: "Upload resources",
            action: () => {
                setUploadResourcesDialogState( true )
            },
            value: "upload"
        }]

    const handleClose = ( event ) => {
        if ( addOptionsRef.current && addOptionsRef.current.contains( event.target ) ) {
            return
        }
        setAddOptionsOpen( false )
    }

    const deleteClassSession = async () => {
        setDeleting( true )
        await deleteSession( classSession )
        setDeleting( false )
    }


    const enableResource = async ( id ) => {
        setEnabling( prev => {
            const newVals = { ...prev }
            newVals[id] = true
            return newVals
        } )
        await LiveSessionsStore.enableResource( classSession.id, id )
        await LiveSessionsStore.fetchLiveSession( classSession.id )
        setEnabling( prev => {
            const newVals = { ...prev }
            newVals[id] = false
            return newVals
        } )
    }

    const disableResource = async ( id ) => {
        setEnabling( prev => {
            const newVals = { ...prev }
            newVals[id] = true
            return newVals
        } )
        await LiveSessionsStore.disableResource( classSession.id, id )
        await LiveSessionsStore.fetchLiveSession( classSession.id )
        setEnabling( prev => {
            const newVals = { ...prev }
            newVals[id] = false
            return newVals
        } )
    }

    const unlinkResource = async ( id ) => {
        await LiveSessionsStore.unlinkResource( classSession.id, id )
        LiveSessionsStore.fetchResources()
    }

    const downloadResource = async ( key, relation_id, details ) => {
        setDownloading( true )
        await LiveSessionsStore.downloadResource( key, classSession.id, relation_id, details )
        setDownloading( false )
    }

    const getData = useCallback( async () => {
        setLoading( true )
        const { accessDenied } = await LiveSessionsStore.fetchLiveSession( classSession.id )
        if ( accessDenied ) {
            const userRole = UserStore.getUser.user_role === 'STUDENT' ? 'student' : "faculty"
            navigate( `/${userRole}/class-sessions` )
        }
        setLoading( false )
    }, [LiveSessionsStore, classSession, UserStore, navigate] )


    useEffect( () => {
        getData()
    }, [getData] )

    return (
        <Box borderRadius="5px" overflow="auto" flexGrow={1} display="flex" flexDirection="column" border={{ md: border[1], xs: "none" }}>
            {!['STUDENT'].includes( UserStore.getUser.user_role ) && selectedAddOption >= 0 && classSession && <AddResourcesDialog classSession={classSession} setLinkMode={setSelectedAddOption} session={LiveSessionsStore?.getLiveSession?.details} setState={setUploadResourcesDialogState} state={uploadResourcesDialogState} />}
            {!['STUDENT'].includes( UserStore.getUser.user_role ) && <CreateClassSessionDialog getData={getData} mode={editNewDialogState ? 'edit' : 'create'} session={LiveSessionsStore?.getLiveSession?.details} subject={classSession?.subject} setState={() => setEditNewDialogState( false )} state={editNewDialogState} />}
            <ConfirmDialog status={deleteConfirmationDialogState} confirmAction={deleteClassSession} message={`All the resource association will be lost!`} actionName="Yes! delete it" cancelAction={() => setDeleteConfirmationDialogState( false )} waiting={deleting} />


            <Box padding="10px" display="flex" alignItems="center" gap="20px" flexWrap="wrap" justifyContent="space-between">
                <Box>
                    <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px" textTransform="capitalize">{classSession.name}</Typography>
                    <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >{LiveSessionsStore.getLiveSession?.resources?.length || 0} resources.</Typography>
                </Box>
                {UserStore.getUser?.user_role !== "STUDENT" && <Box display="flex" flexWrap="wrap" gap="10px" alignItems="center">
                    <Button size='small' onClick={() => { setUploadResourcesDialogState( true ) }} variant="outlined" disableElevation startIcon={Icons.default.AddCircleIcon} sx={{ textTransform: "capitalize" }} color="primary">
                        Add Resources
                    </Button>
                    <IconButton size='small' onClick={() => { setEditNewDialogState( true ) }} variant="outlined" sx={{ color: "GrayText", ":hover": { color: "secondary.main" } }}>
                        {Icons.default.EditIcon}
                    </IconButton>
                    <IconButton size='small' onClick={() => { setDeleteConfirmationDialogState( true ) }} variant="outlined" sx={{ color: "GrayText", ":hover": { color: "error.main" } }} >
                        {Icons.default.DeleteIcon}
                    </IconButton>

                </Box>}
            </Box>
            {!loading && LiveSessionsStore.getLiveSession?.resources?.length > 0 && <Box flexGrow={1} display="flex" flexDirection="column" gap="10px" padding="10px">
                {LiveSessionsStore.getLiveSession?.resources.map( resource => {
                    let ext
                    if ( resource.resource_type === 'document' ) {
                        ext = resource?.resource_metadata?.originalname?.split( "." ).pop()
                        ext = ext.toLowerCase()
                    }
                    return (
                        <Box display="flex" gap="20px" justifyContent="space-between" alignItems="flex-start" border={border[1]} key={resource.id} padding="10px" borderRadius="5px">
                            <Box display="flex" flexGrow={1} justifyContent="space-between" overflow="hidden">
                                <Box minWidth={0} display="flex" alignItems="center" gap={layout.cardsGap}>
                                    <Box sx={{ minWidth: "40px", width: "40px", aspectRatio: "1 / 1" }}>
                                        {resource?.resource_metadata?.resource?.image && <Box flexShrink={0} component="img" width="100%" height="100%" sx={{ objectFit: "cover" }} src={resource?.resource_metadata?.resource?.image} />}
                                        {!resource?.resource_metadata?.resource?.image && resource.resource_type === "link" && <Box color={palette.text.secondary} width="100%" height="100%" display="flex" alignItems="center" justifyContent="center">{Icons.default.Link}</Box>}
                                        {!resource?.resource_metadata?.resource?.image && resource.resource_type === "video" && <Box color={palette.text.secondary} width="100%" height="100%" display="flex" alignItems="center" fontSize="32px" justifyContent="center">{Icons.default.PlayCircleFilledWhiteIcon}</Box>}
                                        {!resource?.resource_metadata?.resource?.image && resource.resource_type === "document" && <Box color={palette.text.secondary} width="100%" height="100%" display="flex" alignItems="center" fontSize="32px" justifyContent="center">{Icons.default.DescriptionIcon}</Box>}
                                    </Box>
                                    <Box minWidth={0}>
                                        <Typography component="span" color='textSecondary' noWrap fontSize="10px">{resource.resource_type}</Typography>
                                        <Tooltip title={resource.name} disableInteractive >
                                            <Typography variant='h6' component="p" noWrap fontSize="14px">{resource.name}</Typography>
                                        </Tooltip>
                                        {resource.enabled_at && <Typography component="span" color='textSecondary' noWrap fontSize="10px">{formatDate( resource.enabled_at )}</Typography>}
                                    </Box>
                                </Box>
                                <Box display="flex" alignItems="center">
                                    {resource.resource_type === 'link' && <Tooltip title="Visit site">
                                        <Link target='_blank' to={`${resource.resource_key}`} >
                                            <IconButton disabled={!resource.is_enabled && UserStore.getUser.user_role === "STUDENT"}>
                                                {Icons.default.OpenInNewIcon}
                                            </IconButton>
                                        </Link>
                                    </Tooltip>}
                                    {resource.resource_type === 'link' && <Tooltip title="Copy link">
                                        <span>
                                            <IconButton onClick={() => copyLink( resource.resource_key )} disabled={!resource.is_enabled && UserStore.getUser.user_role === "STUDENT"}>
                                                {Icons.default.Copy}
                                            </IconButton>
                                        </span>
                                    </Tooltip>}
                                    {resource.resource_type === 'video' && <IconButton onClick={() => handleClickOnVideoResource( resource )} disabled={!resource.is_enabled && UserStore.getUser.user_role === "STUDENT"}>
                                        {Icons.default.PlayCircleFilledWhiteIconOutlined}
                                    </IconButton>}
                                    {( ( resource.resource_type === 'image' || resource.resource_type === 'document' ) && ( !isIOS() || ( isIOS() && ext === 'pdf' ) ) ) && <IconButton disabled={!resource.is_enabled && UserStore.getUser.user_role === "STUDENT"} onClick={() => viewResource( resource, ext )}>
                                        {Icons.default.VisibilityIcon}
                                    </IconButton>}
                                    {( resource.resource_type === 'image' || resource.resource_type === 'document' || resource.resource_type === 'video' ) && <IconButton onClick={() => downloadResource( resource.resource_key, resource.relation_id, resource )} disabled={!resource.is_enabled && UserStore.getUser.user_role === "STUDENT"}>
                                        {downloading ? <CircularProgress size={20} /> : Icons.default.DownloadForOfflineIcon}
                                    </IconButton>}
                                    {UserStore.getUser.user_role !== "STUDENT" && ( enabling[resource.relation_id] ? <CircularProgress size={20} /> : <CustomSwitch size="small" checked={resource.is_enabled} onChange={() => {
                                        if ( resource.is_enabled )
                                            disableResource( resource.relation_id )
                                        else
                                            enableResource( resource.relation_id )
                                    }} /> )}

                                    {UserStore.getUser.user_role !== "STUDENT" && UserStore.getUser.user_id === classSession.creator_user_id && <Tooltip title="Remove this resource">
                                        <IconButton onClick={() => { unlinkResource( resource.relation_id ) }} color="error">{Icons.default.ClearIcon}</IconButton>
                                    </Tooltip>}
                                </Box>
                            </Box>
                        </Box>
                    )
                } )}
            </Box>}
            {!loading && LiveSessionsStore.getLiveSession?.resources?.length === 0 && <Box height="100%" margin="10px" borderRadius="5px" bgcolor={palette.contentBg} flexGrow={1} display="flex" flexDirection="column" alignItems="center" padding="20px" justifyContent="center" gap="20px">
                <Box component="img" src={noResourcesImg} width="150px" alt="No resources" />
                <Typography variant="subtitle2">{UserStore.getUser?.user_role !== "STUDENT" ? "You haven't added any resources to sessions!" : "This beyond class resource session does not contain any resources yet!"}</Typography>
                {UserStore.getUser?.user_role !== "STUDENT" && <Button onClick={addOptions[selectedAddOption].action} startIcon={Icons.default.AddIcon} variant='outlined' color="primaryDark" sx={{ textTransform: "capitalize" }} >Add resources</Button>}
            </Box>}
            {loading && <Box margin="10px" borderRadius="5px" bgcolor={palette.contentBg} flexGrow={1} display="flex" flexDirection="column" alignItems="center" padding="20px" justifyContent="center" gap="20px">
                <DataLoadingSpinner waitingMessage="Loading resources..." />
            </Box>}
        </Box>
    )
} )

const SubjectClassSessions = observer( () => {


    const [error, setError] = useState( null )
    const [subjects, setSubjects] = useState( [] )
    const [loading, setLoading] = useState( true )
    const [videoSrc, setVideoSrc] = useState( null )
    const [deleting, setDeleting] = useState( false )
    const [previewFile, setPreviewFile] = useState( null )
    const [selectedResource, setSelectedResource] = useState( null )
    const [videoPlayerControls, setVideoPlayerControls] = useState( {} )
    const [videoPlayerDefaults, setVideoPlayerDefaults] = useState( {} )
    const [mobileDrawerVisible, setMobileDrawerVisible] = useState( false )
    const [createNewDialogState, setCreateNewDialogState] = useState( false )
    const [previewingFileDetails, setPreviewingFileDetails] = useState( null )

    const [retrievingLegacyData, setRetrievingLegacyData] = useState( false )
    const [selectedClassResource, setSelectedClassResource] = useState( null )

    const UserStore = useUserStore()
    const LiveSessionsStore = useLiveSessionStore()
    const [selectedSubject, setSelectedSubject] = useState( null )

    const { palette, layout, border, breakpoints } = useTheme()
    const smallViewport = useMediaQuery( breakpoints.down( 'md' ) )

    const copyLink = ( url ) => {
        window.navigator.clipboard.writeText( convertToCDNLink( url ) )
        toast( "Copied link successfully" )
    }


    const deleteSession = async ( classSession ) => {
        try {
            if ( classSession )
                await api.liveSessions.deleteLiveSession( classSession.id )
            getData()
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while deleting the class session" )
        }
    }

    const handleClickOnVideoResource = async ( resource ) => {
        try {
            const { data: { data } } = await api.liveSessions.fetchLiveSessionResourceLink( selectedClassResource.id, resource.relation_id, { key: resource.resource_key } )

            if ( UserStore.getUser.user_role === 'STUDENT' ) {
                setVideoPlayerControls( { volume: false } )
                setVideoPlayerDefaults( { volume: 0 } )
            } else {
                setVideoPlayerControls( {} )
                setVideoPlayerDefaults( {} )
            }
            setVideoSrc( convertToCDNLink( data ) )
            setSelectedResource( resource )
        } catch ( err ) {
            toast( err?.responce?.data?.message || "Couldn't fetch session resource!" )
        }
    }

    const viewResource = async ( resource, ext ) => {
        try {
            const { data: { data } } = await api.liveSessions.fetchLiveSessionResourceLink( selectedClassResource.id, resource.relation_id, { key: resource.resource_key } )
            setPreviewFile( convertToCDNLink( data ) )
            setPreviewingFileDetails( { name: resource.name, type: ext } )
        } catch ( err ) {
            console.log( err )
            toast( err?.responce?.data?.message || "Couldn't fetch session resource!" )
        }
    }


    const retrieveLegacyClassSessions = async () => {
        try {
            setRetrievingLegacyData( true )
            await api.liveSessions.retrieveLegacyAliveLivesessions()
            setLoading( true )
            await UserStore.fetchUser()
            await LiveSessionsStore.fetchLiveSessions()
            setLoading( false )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while retriving legacy class sessions!" )
        } finally {
            setRetrievingLegacyData( false )
        }
    }

    const handleSubjectChange = ( event ) => {
        try {
            const subjectId = event.target.value
            const subject = subjects?.find( sub => sub.subject_id === subjectId )
            setSelectedSubject( subject )
            const subjectClassResources = LiveSessionsStore.getLiveSessions.filter( cr => String( cr.subject?.subject_id ) === String( subjectId ) )
            setSelectedClassResource( smallViewport ? null : subjectClassResources[0] )
        } catch ( err ) {
            console.log( err )
        }
    }


    const getData = useCallback( async () => {
        const responseDetails = await LiveSessionsStore.fetchLiveSessions()
        if ( responseDetails.success ) {
            setSelectedClassResource( window.innerWidth > 900 ? LiveSessionsStore.getLiveSessions[0] : null )
            const recourceSubjects = LiveSessionsStore.getLiveSessions.map( cr => cr.subject )
            const availableSubjects = []
            let uniqueSubjects = {}
            for ( const sub of recourceSubjects ) {
                if ( !uniqueSubjects[sub.subject_id] ) {
                    availableSubjects.push( sub )
                    uniqueSubjects[sub.subject_id] = true
                }
            }

            setSubjects( availableSubjects )
            setSelectedSubject( availableSubjects[0] )
            setError( null )
        } else {
            setError( responseDetails )
        }
        setLoading( false )
    }, [LiveSessionsStore] )

    useEffect( () => {
        getData()
    }, [LiveSessionsStore, getData] )

    const filteredClassResources = LiveSessionsStore.getLiveSessions.filter( cr => String( cr.subject?.subject_id ) === String( selectedSubject?.subject_id ) )

    return (
        <Box flexGrow={1} display="flex" gap="20px" padding={layout.pagePadding} overflow="auto" flexDirection="column">
            {/* DIALOGS */}
            {'STUDENT' !== UserStore.getUser.user_role && <CreateClassSessionDialog getData={getData} subject={selectedSubject} setState={() => setCreateNewDialogState( false )} state={createNewDialogState} />}

            <Dialog open={Boolean( videoSrc && selectedResource )} onClose={() => { setVideoSrc( null ); setSelectedResource( null ) }}>
                {
                    videoSrc && selectedResource &&
                    <VideoPlayer
                        src={videoSrc}
                        title={selectedResource.name}
                        controllerOptions={videoPlayerControls}
                        defaults={videoPlayerDefaults}
                    />}
            </Dialog>
            <Drawer anchor='bottom' sx={{ zIndex: 9000000 }} open={Boolean( previewFile )} slotProps={{
                paper: { sx: { height: "calc(100% - 50px)" } }
            }} >
                <Box bgcolor={palette.cardBackground} flexGrow={1} display="flex" overflow="auto" flexDirection="column" borderRadius="20px 20px 0 0">
                    <Box borderBottom={border[1]} alignItems="center" display="flex" justifyContent="space-between" gap="20px" padding="10px 20px">
                        <Typography variant='h6'>{previewingFileDetails?.name}</Typography>
                        <Box display="flex" gap="10px" alignItems="center">
                            <IconButton onClick={() => { setPreviewFile( null ); setPreviewingFileDetails( null ) }}>
                                {Icons.default.CloseIcon}
                            </IconButton>
                        </Box>
                    </Box>
                    <Box padding="20px" display="flex" flexDirection="column" overflow="auto" flexGrow={1} height="300px">
                        <DocumentViewer loadingText="Loading material..." setFile={setPreviewFile} file={previewFile} type={previewingFileDetails?.type} />
                    </Box>
                </Box>
            </Drawer>


            {/* MAIN CONTENT */}

            <Box display="flex" justifyContent="space-between" flexWrap="wrap" alignItems="center" gap="10px" >
                <Box position="relative"  >
                    <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Beyond class resources</Typography>
                    <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Share resources which helps student understand concepts better.</Typography>
                </Box>

                {UserStore.getUser?.user_role !== "STUDENT" && <Box display="flex" gap="10px" flexWrap="wrap" justifyContent="flex-end" alignItems="center">
                    <Button onClick={() => setCreateNewDialogState( true )} variant="contained" disableElevation startIcon={Icons.default.AddIcon} sx={{ textTransform: "capitalize" }} color="primary">
                        Create Session
                    </Button>
                    {!UserStore.getUser?.data_sync_status?.livesessions && <Button onClick={retrieveLegacyClassSessions} variant="contained" disableElevation disabled={retrievingLegacyData} startIcon={retrievingLegacyData ? <CircularProgress size={14} /> : Icons.default.Replay} sx={{ textTransform: "capitalize" }} color="secondary">
                        Retrieve Old class sessions
                    </Button>}
                </Box>}
            </Box>
            {!loading && filteredClassResources.length > 0 && <Box gap="20px" borderRadius="5px" flexGrow={1} display="grid" gridTemplateColumns={{ md: "280px auto", xs: "1fr" }} overflow="auto" >
                <Box display="flex" flexDirection="column">
                    <Select
                        fullWidth
                        size="small"
                        displayEmpty
                        sx={{
                            textTransform: "capitalize",
                            "&.Mui-focused": {
                                boxShadow: "none", // disabing glow effect outside
                                border: border[1]
                            },
                        }}
                        value={selectedSubject.subject_id || ""}
                        onChange={handleSubjectChange}
                        name="subject"
                        id="resource-subject"
                    >
                        <MenuItem value="" dense disabled sx={{ fontWeight: "400" }} >{loading ? "Loading ..." : "Eg: Data structures"}</MenuItem>
                        {!loading && ( subjects || [] ).map( ( sub ) => {
                            return ( <MenuItem sx={{ textTransform: "capitalize" }} dense key={sub?.subject_id} value={sub?.subject_id}>
                                {`${sub.subject_name?.toLowerCase()} (${sub.subject_code})`}
                            </MenuItem> )
                        } )
                        }
                    </Select>
                    <Box flexGrow={1} marginTop="10px" padding="5px" border={border[1]} borderRadius="5px" display="flex" flexDirection="column" gap="10px">
                        {filteredClassResources.map( cr => (
                            <Box display="flex" alignItems='flex-start' sx={{
                                cursor: "pointer", ":hover": {
                                    bgcolor: selectedClassResource?.id !== cr.id && palette.background.paper + "4"
                                }
                            }}
                                gap="10px"
                                onClick={() => {
                                    setSelectedClassResource( cr )
                                    setMobileDrawerVisible( true )
                                }}
                                border={border[1]}
                                borderColor={selectedClassResource?.id === cr.id ? palette.primary.light : palette.borderColor}
                                bgcolor={selectedClassResource?.id === cr.id ? palette.contentBg : palette.background.paper}
                                padding="10px"
                                borderRadius="5px"
                                key={cr.id}
                            >
                                <Box flexGrow={1}>
                                    <Typography variant='h6' component="p" fontSize="14px" textTransform="capitalize">{cr.name}</Typography>
                                    {UserStore.getUser.user_role === "STUDENT" && <Typography color={"textSecondary"} display="flex" alignItems="center" gap="5px" variant='h6' component="p" fontSize="12px" textTransform="capitalize"> {Icons.default.User} {cr.faculty_details?.name?.toLowerCase()}</Typography>}
                                    {UserStore.getUser.user_role !== "STUDENT" && <Box display="flex" gap="5px" flexWrap="wrap" alignItems="center">
                                        {cr.access_groups.map( ( group, index ) => {
                                            const groupDetails = getTransformedGroupDetails( group )
                                            return (
                                                <CustomChip tooltipPlacement="top-start" tooltipTitle={groupDetails.academic_details} variant='filled' startIcon={Icons.default.Group} key={index} value={groupDetails.chip_label} />
                                            )
                                        } )}
                                    </Box>}
                                    <Box display="flex" gap="5px" flexWrap="wrap" marginTop="5px" alignItems="center">
                                        <CustomChip color="greyed" value={formatDate( cr.updated_at )} startIcon={Icons.default.Calendar} tooltipTitle='Last updated' />
                                        <CustomChip color="greyed" value={`Resources: ${cr.resource_count || 0}`} startIcon={Icons.default.Calendar} />
                                    </Box>
                                </Box>
                                <Radio sx={{ display: { md: "block", xs: "none" } }} size='small' checked={selectedClassResource?.id === cr.id} />
                                <Box fontSize="20px" display={{ md: "none", xs: "flex" }} height="100%" alignItems="center" color={palette.common.font}>
                                    {Icons.default.ChevronRightIcon}
                                </Box>
                            </Box>
                        ) )}
                    </Box>
                </Box>
                {selectedClassResource && <Box overflow="auto" display={{ md: "block", xs: "none" }}>
                    <ClassSessionDetails deleteSession={deleteSession} copyLink={copyLink} handleClickOnVideoResource={handleClickOnVideoResource} viewResource={viewResource} classSession={selectedClassResource} />
                </Box>}
                <SwipeableDrawer sx={{ display: { md: "none", xs: "block" } }} slotProps={{
                    paper: {
                        sx: {
                            height: "80%",
                            border: "none",
                            borderRadius: "10px 10px 0 0",
                        }
                    }
                }} anchor='bottom' onClose={() => setMobileDrawerVisible( false )} open={mobileDrawerVisible && Boolean( selectedClassResource )} >
                    <Box display="flex" justifyContent="center" padding="5px">
                        <Box height="10px" borderRadius="10px" width="100px" bgcolor={palette.action.selected} />
                    </Box>
                    {selectedClassResource && <ClassSessionDetails deleteSession={deleteSession} copyLink={copyLink} handleClickOnVideoResource={handleClickOnVideoResource} viewResource={viewResource} classSession={selectedClassResource} />}
                    <Box padding="5px">
                        <Button variant='outlined' color='greyed' size='small' fullWidth onClick={() => setMobileDrawerVisible( false )}>Close</Button>
                    </Box>
                </SwipeableDrawer>
            </Box>}

            {!error && !loading && filteredClassResources.length === 0 && <Box borderRadius="5px" bgcolor={palette.contentBg} flexGrow={1} display="flex" flexDirection="column" alignItems="center" padding="20px" justifyContent="center" gap="20px">
                <Box component="img" src={noSessionsImg} width="150px" alt="No quizes" />
                <Typography variant="subtitle2">{UserStore.getUser?.user_role !== "STUDENT" ? "You does not have any live sessions" : "Beyond class resources are not uploaded for your batches or section!"}</Typography>
                {UserStore.getUser?.user_role !== "STUDENT" && <Button onClick={() => setCreateNewDialogState( true )} startIcon={Icons.default.AddIcon} variant='outlined' color="primaryDark" sx={{ textTransform: "capitalize" }} >Create one</Button>}
            </Box>}
            {!error && loading && <DataLoadingSpinner padding='0' waitingMessage="Loading sessions..." />}
            {error && <TryAgainMessageBlock code={error.code} err={error.message} getData={getData} />}
        </Box>
    )
} )

export default SubjectClassSessions
